import React, { useEffect } from 'react';

const SQLvsNoSQL = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'SQL vs NoSQL Databases - Complete Comparison Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Complete SQL vs NoSQL comparison. Learn RDBMS vs Document, Key-Value, Column-Family, Graph databases. When to use each with examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Complete SQL vs NoSQL comparison. Learn RDBMS vs Document, Key-Value, Column-Family, Graph databases. When to use each with examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'SQL vs NoSQL, relational database, document database, key-value store, column family, graph database, ACID, CAP theorem');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'SQL vs NoSQL, relational database, document database, key-value store, column family, graph database, ACID, CAP theorem';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>SQL vs NoSQL Databases</h1>
        <p style={styles.subtitle}>
          Complete Comparison Guide - When to Use Each Database Type
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Overview</h2>
          <p style={styles.paragraph}>
            The choice between SQL and NoSQL databases is one of the most important architectural 
            decisions in system design. SQL databases offer strong consistency and ACID properties, 
            while NoSQL databases provide flexibility and horizontal scalability. Understanding their 
            strengths and trade-offs is crucial for building effective systems.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>SQL Databases (RDBMS)</h2>
          <p style={styles.paragraph}>
            SQL databases are relational databases that store data in structured tables with predefined 
            schemas. They use SQL (Structured Query Language) for querying and maintaining data integrity 
            through ACID properties.
          </p>

          <div style={styles.codeBlock}>
{`-- SQL Database Example: E-commerce Schema

-- Users table with constraints
CREATE TABLE users (
    user_id INT AUTO_INCREMENT PRIMARY KEY,
    username VARCHAR(50) UNIQUE NOT NULL,
    email VARCHAR(100) UNIQUE NOT NULL,
    password_hash VARCHAR(255) NOT NULL,
    first_name VARCHAR(50),
    last_name VARCHAR(50),
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP
);

-- Products table
CREATE TABLE products (
    product_id INT AUTO_INCREMENT PRIMARY KEY,
    name VARCHAR(200) NOT NULL,
    description TEXT,
    price DECIMAL(10,2) NOT NULL,
    stock_quantity INT DEFAULT 0,
    category_id INT,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    
    FOREIGN KEY (category_id) REFERENCES categories(category_id),
    INDEX idx_products_category (category_id),
    INDEX idx_products_price (price)
);

-- Orders table with foreign key relationships
CREATE TABLE orders (
    order_id INT AUTO_INCREMENT PRIMARY KEY,
    user_id INT NOT NULL,
    total_amount DECIMAL(10,2) NOT NULL,
    status ENUM('pending', 'processing', 'shipped', 'delivered', 'cancelled') DEFAULT 'pending',
    shipping_address TEXT,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    
    FOREIGN KEY (user_id) REFERENCES users(user_id),
    INDEX idx_orders_user (user_id),
    INDEX idx_orders_status (status),
    INDEX idx_orders_date (created_at)
);

-- Order items junction table
CREATE TABLE order_items (
    order_item_id INT AUTO_INCREMENT PRIMARY KEY,
    order_id INT NOT NULL,
    product_id INT NOT NULL,
    quantity INT NOT NULL,
    unit_price DECIMAL(10,2) NOT NULL,
    
    FOREIGN KEY (order_id) REFERENCES orders(order_id) ON DELETE CASCADE,
    FOREIGN KEY (product_id) REFERENCES products(product_id),
    UNIQUE KEY uk_order_product (order_id, product_id)
);

-- Complex queries with joins
SELECT 
    u.username,
    u.email,
    o.order_id,
    o.total_amount,
    o.status,
    COUNT(oi.order_item_id) as item_count,
    GROUP_CONCAT(p.name) as products
FROM users u
JOIN orders o ON u.user_id = o.user_id
JOIN order_items oi ON o.order_id = oi.order_id
JOIN products p ON oi.product_id = p.product_id
WHERE o.created_at >= DATE_SUB(NOW(), INTERVAL 30 DAY)
    AND o.status IN ('delivered', 'shipped')
GROUP BY u.user_id, o.order_id
HAVING item_count > 2
ORDER BY o.total_amount DESC;

-- Transaction example
START TRANSACTION;

-- Update inventory
UPDATE products 
SET stock_quantity = stock_quantity - 2 
WHERE product_id = 123;

-- Create order
INSERT INTO orders (user_id, total_amount, status) 
VALUES (456, 99.98, 'pending');

-- Add order items
INSERT INTO order_items (order_id, product_id, quantity, unit_price)
VALUES (LAST_INSERT_ID(), 123, 2, 49.99);

COMMIT;`}
          </div>

          <h3 style={styles.heading}>SQL Database Characteristics</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>ACID Compliance:</strong> Atomicity, Consistency, Isolation, Durability</li>
            <li style={styles.listItem}><strong>Schema-based:</strong> Predefined structure with strict data types</li>
            <li style={styles.listItem}><strong>Relational:</strong> Data relationships through foreign keys</li>
            <li style={styles.listItem}><strong>SQL Language:</strong> Standardized query language</li>
            <li style={styles.listItem}><strong>Vertical Scaling:</strong> Scale up by adding more power to existing servers</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>NoSQL Databases</h2>
          <p style={styles.paragraph}>
            NoSQL databases are non-relational databases designed to handle large volumes of 
            unstructured or semi-structured data. They sacrifice some ACID properties for better 
            scalability and flexibility.
          </p>

          <h3 style={styles.heading}>1. Document Databases</h3>
          <p style={styles.paragraph}>
            Store data as documents (typically JSON) with flexible schemas. Examples: MongoDB, CouchDB.
          </p>

          <div style={styles.codeBlock}>
{`// MongoDB Document Database Example

// User document
{
  "_id": ObjectId("507f1f77bcf86cd799439011"),
  "username": "john_doe",
  "email": "john@example.com",
  "profile": {
    "firstName": "John",
    "lastName": "Doe",
    "age": 30,
    "address": {
      "street": "123 Main St",
      "city": "Anytown",
      "zipCode": "12345",
      "country": "USA"
    },
    "preferences": {
      "newsletter": true,
      "notifications": ["email", "push"],
      "theme": "dark"
    }
  },
  "orders": [
    {
      "orderId": "ORD-001",
      "date": ISODate("2024-01-15"),
      "total": 99.99,
      "status": "delivered",
      "items": [
        {
          "productId": "PROD-123",
          "name": "Wireless Headphones",
          "quantity": 1,
          "price": 99.99
        }
      ]
    }
  ],
  "createdAt": ISODate("2023-12-01"),
  "updatedAt": ISODate("2024-01-15")
}

// Product catalog document
{
  "_id": ObjectId("507f1f77bcf86cd799439012"),
  "sku": "PROD-123",
  "name": "Wireless Headphones",
  "description": "High-quality wireless headphones with noise cancellation",
  "price": 99.99,
  "category": "Electronics",
  "subcategory": "Audio",
  "specifications": {
    "brand": "TechBrand",
    "model": "WH-1000XM4",
    "color": "Black",
    "weight": "254g",
    "batteryLife": "30 hours",
    "features": ["noise-cancellation", "wireless", "bluetooth-5.0"]
  },
  "inventory": {
    "inStock": 150,
    "reserved": 10,
    "warehouse": "US-WEST"
  },
  "reviews": [
    {
      "userId": ObjectId("507f1f77bcf86cd799439011"),
      "rating": 5,
      "comment": "Excellent sound quality!",
      "date": ISODate("2024-01-20")
    }
  ],
  "tags": ["wireless", "headphones", "premium", "noise-cancelling"],
  "createdAt": ISODate("2023-11-01"),
  "updatedAt": ISODate("2024-01-20")
}

// MongoDB Queries
// Find users with recent orders
db.users.find({
  "orders.date": { $gte: new Date("2024-01-01") },
  "orders.status": "delivered"
})

// Complex aggregation pipeline
db.users.aggregate([
  { $unwind: "$orders" },
  { $match: { 
    "orders.date": { $gte: new Date("2024-01-01") },
    "orders.total": { $gt: 50 }
  }},
  { $group: {
    _id: "$_id",
    username: { $first: "$username" },
    totalSpent: { $sum: "$orders.total" },
    orderCount: { $sum: 1 }
  }},
  { $sort: { totalSpent: -1 } },
  { $limit: 10 }
])

// Update nested document
db.users.updateOne(
  { "_id": ObjectId("507f1f77bcf86cd799439011") },
  { 
    $push: { 
      "orders": {
        "orderId": "ORD-002",
        "date": new Date(),
        "total": 149.99,
        "status": "pending"
      }
    }
  }
)`}
          </div>

          <h3 style={styles.heading}>2. Key-Value Stores</h3>
          <p style={styles.paragraph}>
            Simple key-value pairs for fast lookups. Examples: Redis, DynamoDB, Riak.
          </p>

          <div style={styles.codeBlock}>
{`// Redis Key-Value Store Examples

// Simple key-value operations
SET user:1001:name "John Doe"
SET user:1001:email "john@example.com"
SET user:1001:last_login "2024-01-15T10:30:00Z"

GET user:1001:name
// Returns: "John Doe"

// Hash operations for structured data
HSET user:1001 name "John Doe" email "john@example.com" age 30
HGET user:1001 name
HGETALL user:1001

// Session management
SET session:abc123 "user_id=1001&role=admin&expires=1642176000" EX 3600
GET session:abc123

// Shopping cart (using lists)
LPUSH cart:user:1001 "product:123"
LPUSH cart:user:1001 "product:456"
LRANGE cart:user:1001 0 -1

// Product views counter
INCR product:123:views
INCRBY product:123:views 5

// Real-time analytics (using sorted sets)
ZADD daily_sales:2024-01-15 99.99 "order:1001"
ZADD daily_sales:2024-01-15 149.99 "order:1002"
ZRANGE daily_sales:2024-01-15 0 -1 WITHSCORES

// Caching with expiration
SETEX cache:product:123 300 '{"name":"Headphones","price":99.99}'

// Pub/Sub for real-time features
PUBLISH notifications:user:1001 '{"type":"order_shipped","orderId":"ORD-001"}'
SUBSCRIBE notifications:user:1001

// Geospatial data
GEOADD stores:locations -122.4194 37.7749 "store:sf"
GEOADD stores:locations -74.0059 40.7128 "store:nyc"
GEORADIUS stores:locations -122.4194 37.7749 10 km`}
          </div>

          <h3 style={styles.heading}>3. Column-Family</h3>
          <p style={styles.paragraph}>
            Store data in column families, optimized for write-heavy workloads. Examples: Cassandra, HBase.
          </p>

          <div style={styles.codeBlock}>
{`// Cassandra Column-Family Example

-- Create keyspace (database)
CREATE KEYSPACE ecommerce 
WITH REPLICATION = {
  'class': 'SimpleStrategy',
  'replication_factor': 3
};

-- User profiles table
CREATE TABLE ecommerce.user_profiles (
    user_id UUID PRIMARY KEY,
    username TEXT,
    email TEXT,
    first_name TEXT,
    last_name TEXT,
    created_at TIMESTAMP,
    updated_at TIMESTAMP
);

-- Time-series data for user activities
CREATE TABLE ecommerce.user_activities (
    user_id UUID,
    activity_time TIMESTAMP,
    activity_type TEXT,
    details MAP<TEXT, TEXT>,
    PRIMARY KEY (user_id, activity_time)
) WITH CLUSTERING ORDER BY (activity_time DESC);

-- Product catalog with wide rows
CREATE TABLE ecommerce.product_catalog (
    category TEXT,
    product_id UUID,
    name TEXT,
    price DECIMAL,
    description TEXT,
    specifications MAP<TEXT, TEXT>,
    tags SET<TEXT>,
    PRIMARY KEY (category, product_id)
);

-- Shopping cart (using collections)
CREATE TABLE ecommerce.shopping_carts (
    user_id UUID PRIMARY KEY,
    items MAP<UUID, INT>,  -- product_id -> quantity
    updated_at TIMESTAMP
);

-- Insert data
INSERT INTO ecommerce.user_profiles (user_id, username, email, first_name, last_name, created_at)
VALUES (uuid(), 'john_doe', 'john@example.com', 'John', 'Doe', toTimestamp(now()));

-- Query by partition key
SELECT * FROM ecommerce.user_activities 
WHERE user_id = 550e8400-e29b-41d4-a716-446655440000
AND activity_time >= '2024-01-01';

-- Update collections
UPDATE ecommerce.shopping_carts 
SET items = items + {550e8400-e29b-41d4-a716-446655440001: 2}
WHERE user_id = 550e8400-e29b-41d4-a716-446655440000;

-- Batch operations
BEGIN BATCH
  INSERT INTO ecommerce.user_activities (user_id, activity_time, activity_type, details)
  VALUES (uuid(), toTimestamp(now()), 'purchase', {'order_id': 'ORD-001', 'amount': '99.99'});
  
  UPDATE ecommerce.shopping_carts SET items = {} 
  WHERE user_id = 550e8400-e29b-41d4-a716-446655440000;
APPLY BATCH;`}
          </div>

          <h3 style={styles.heading}>4. Graph Databases</h3>
          <p style={styles.paragraph}>
            Store data as nodes and relationships, ideal for connected data. Examples: Neo4j, Amazon Neptune.
          </p>

          <div style={styles.codeBlock}>
{`// Neo4j Graph Database Example (Cypher Query Language)

// Create nodes
CREATE (john:User {id: 1001, name: 'John Doe', email: 'john@example.com'})
CREATE (alice:User {id: 1002, name: 'Alice Smith', email: 'alice@example.com'})
CREATE (headphones:Product {id: 'PROD-123', name: 'Wireless Headphones', price: 99.99})
CREATE (laptop:Product {id: 'PROD-456', name: 'Gaming Laptop', price: 1299.99})
CREATE (electronics:Category {name: 'Electronics'})

// Create relationships
CREATE (john)-[:PURCHASED {date: '2024-01-15', quantity: 1}]->(headphones)
CREATE (alice)-[:PURCHASED {date: '2024-01-10', quantity: 1}]->(laptop)
CREATE (john)-[:VIEWED {timestamp: '2024-01-14T10:30:00'}]->(laptop)
CREATE (alice)-[:VIEWED {timestamp: '2024-01-12T15:45:00'}]->(headphones)
CREATE (john)-[:FOLLOWS]->(alice)
CREATE (headphones)-[:BELONGS_TO]->(electronics)
CREATE (laptop)-[:BELONGS_TO]->(electronics)

// Recommendation engine: Find products purchased by friends
MATCH (user:User {id: 1001})-[:FOLLOWS]->(friend:User)-[:PURCHASED]->(product:Product)
WHERE NOT (user)-[:PURCHASED]->(product)
RETURN product.name, product.price, COUNT(*) as friend_purchases
ORDER BY friend_purchases DESC

// Find similar users based on purchase history
MATCH (user1:User {id: 1001})-[:PURCHASED]->(product:Product)<-[:PURCHASED]-(user2:User)
WHERE user1 <> user2
RETURN user2.name, COUNT(product) as common_purchases
ORDER BY common_purchases DESC

// Product recommendation based on viewing patterns
MATCH (user:User {id: 1001})-[:VIEWED]->(viewed_product:Product)-[:BELONGS_TO]->(category:Category)<-[:BELONGS_TO]-(recommended:Product)
WHERE NOT (user)-[:PURCHASED]->(recommended)
AND NOT (user)-[:VIEWED]->(recommended)
RETURN recommended.name, recommended.price, category.name
LIMIT 5

// Social network analysis: Find influencers
MATCH (user:User)-[:FOLLOWS]->(influencer:User)
RETURN influencer.name, COUNT(*) as followers
ORDER BY followers DESC
LIMIT 10

// Path finding: Degrees of separation
MATCH path = shortestPath((user1:User {id: 1001})-[:FOLLOWS*]-(user2:User {id: 1005}))
RETURN length(path) as degrees_of_separation

// Complex traversal: Products bought by friends of friends
MATCH (user:User {id: 1001})-[:FOLLOWS*2]->(friend_of_friend:User)-[:PURCHASED]->(product:Product)
WHERE NOT (user)-[:PURCHASED]->(product)
RETURN DISTINCT product.name, product.price, COUNT(*) as popularity
ORDER BY popularity DESC`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Detailed Comparison</h2>
          
          <div style={styles.codeBlock}>
{`| Aspect              | SQL Databases        | NoSQL Databases      |
|---------------------|----------------------|----------------------|
| Data Model          | Tables, Rows, Columns| Documents, Key-Value, |
|                     |                      | Column, Graph        |
| Schema              | Fixed, Predefined    | Flexible, Dynamic    |
| Scaling             | Vertical (Scale Up)  | Horizontal (Scale Out)|
| ACID Properties     | Full ACID Support    | Eventual Consistency |
| Query Language      | SQL (Standardized)   | Database-specific    |
| Transactions        | Multi-row ACID       | Limited/Eventual     |
| Relationships       | Foreign Keys, Joins  | Embedded/References  |
| Consistency         | Strong Consistency   | Eventual Consistency |
| Performance         | Complex Queries      | Simple Queries       |
| Use Cases           | OLTP, Complex Queries| Big Data, Real-time  |
| Examples            | MySQL, PostgreSQL    | MongoDB, Redis       |`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use SQL Databases</h2>
          
          <h3 style={styles.heading}>Ideal Scenarios</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>ACID Requirements:</strong> Financial systems, banking, e-commerce transactions</li>
            <li style={styles.listItem}><strong>Complex Relationships:</strong> ERP systems, CRM, inventory management</li>
            <li style={styles.listItem}><strong>Reporting & Analytics:</strong> Business intelligence, data warehousing</li>
            <li style={styles.listItem}><strong>Structured Data:</strong> Well-defined schemas, consistent data formats</li>
            <li style={styles.listItem}><strong>Complex Queries:</strong> Multi-table joins, aggregations, subqueries</li>
          </ul>

          <div style={styles.codeBlock}>
{`// Example: Financial Transaction System
CREATE TABLE accounts (
    account_id INT PRIMARY KEY,
    account_number VARCHAR(20) UNIQUE NOT NULL,
    balance DECIMAL(15,2) NOT NULL DEFAULT 0.00,
    account_type ENUM('checking', 'savings', 'credit') NOT NULL,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE transactions (
    transaction_id BIGINT AUTO_INCREMENT PRIMARY KEY,
    from_account_id INT,
    to_account_id INT,
    amount DECIMAL(15,2) NOT NULL,
    transaction_type ENUM('transfer', 'deposit', 'withdrawal') NOT NULL,
    status ENUM('pending', 'completed', 'failed') DEFAULT 'pending',
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    
    FOREIGN KEY (from_account_id) REFERENCES accounts(account_id),
    FOREIGN KEY (to_account_id) REFERENCES accounts(account_id),
    
    -- Ensure amount is positive
    CONSTRAINT chk_positive_amount CHECK (amount > 0)
);

-- ACID Transaction for money transfer
DELIMITER //
CREATE PROCEDURE transfer_money(
    IN from_acc INT,
    IN to_acc INT,
    IN transfer_amount DECIMAL(15,2)
)
BEGIN
    DECLARE EXIT HANDLER FOR SQLEXCEPTION
    BEGIN
        ROLLBACK;
        RESIGNAL;
    END;
    
    START TRANSACTION;
    
    -- Check sufficient balance
    IF (SELECT balance FROM accounts WHERE account_id = from_acc) < transfer_amount THEN
        SIGNAL SQLSTATE '45000' SET MESSAGE_TEXT = 'Insufficient funds';
    END IF;
    
    -- Debit from source account
    UPDATE accounts 
    SET balance = balance - transfer_amount 
    WHERE account_id = from_acc;
    
    -- Credit to destination account
    UPDATE accounts 
    SET balance = balance + transfer_amount 
    WHERE account_id = to_acc;
    
    -- Log transaction
    INSERT INTO transactions (from_account_id, to_account_id, amount, transaction_type, status)
    VALUES (from_acc, to_acc, transfer_amount, 'transfer', 'completed');
    
    COMMIT;
END //
DELIMITER ;`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use NoSQL Databases</h2>
          
          <h3 style={styles.heading}>Ideal Scenarios</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Rapid Development:</strong> Agile development, changing requirements</li>
            <li style={styles.listItem}><strong>Horizontal Scaling:</strong> High traffic, distributed systems</li>
            <li style={styles.listItem}><strong>Unstructured Data:</strong> JSON documents, varied data formats</li>
            <li style={styles.listItem}><strong>Real-time Applications:</strong> Gaming, IoT, social media</li>
            <li style={styles.listItem}><strong>Big Data:</strong> Analytics, logging, time-series data</li>
          </ul>

          <div style={styles.codeBlock}>
{`// Example: Social Media Platform (MongoDB)

// User profile with flexible schema
{
  "_id": ObjectId("..."),
  "username": "tech_guru",
  "profile": {
    "displayName": "Tech Guru",
    "bio": "Passionate about technology and innovation",
    "avatar": "https://cdn.example.com/avatars/tech_guru.jpg",
    "location": "San Francisco, CA",
    "website": "https://techguru.blog",
    "joinDate": ISODate("2023-01-15"),
    "verified": true,
    "badges": ["early_adopter", "tech_expert", "community_leader"]
  },
  "stats": {
    "followers": 15420,
    "following": 892,
    "posts": 1247,
    "likes": 45678
  },
  "preferences": {
    "privacy": "public",
    "notifications": {
      "email": true,
      "push": false,
      "mentions": true,
      "directMessages": true
    },
    "theme": "dark",
    "language": "en"
  },
  "socialLinks": [
    {"platform": "twitter", "url": "https://twitter.com/techguru"},
    {"platform": "linkedin", "url": "https://linkedin.com/in/techguru"},
    {"platform": "github", "url": "https://github.com/techguru"}
  ]
}

// Posts collection with embedded comments
{
  "_id": ObjectId("..."),
  "authorId": ObjectId("..."),
  "content": {
    "text": "Just discovered this amazing new framework! 🚀",
    "media": [
      {
        "type": "image",
        "url": "https://cdn.example.com/posts/framework-demo.jpg",
        "alt": "Framework demo screenshot"
      }
    ],
    "hashtags": ["#javascript", "#framework", "#webdev"],
    "mentions": ["@john_dev", "@react_team"]
  },
  "engagement": {
    "likes": 234,
    "shares": 45,
    "comments": 28,
    "views": 1567
  },
  "comments": [
    {
      "commentId": ObjectId("..."),
      "authorId": ObjectId("..."),
      "text": "This looks incredible! Can't wait to try it.",
      "timestamp": ISODate("2024-01-15T14:30:00Z"),
      "likes": 12,
      "replies": [
        {
          "replyId": ObjectId("..."),
          "authorId": ObjectId("..."),
          "text": "Same here! The documentation looks comprehensive.",
          "timestamp": ISODate("2024-01-15T15:45:00Z"),
          "likes": 3
        }
      ]
    }
  ],
  "createdAt": ISODate("2024-01-15T12:00:00Z"),
  "updatedAt": ISODate("2024-01-15T15:45:00Z")
}

// Real-time feed generation
db.posts.aggregate([
  // Get posts from followed users
  {
    $lookup: {
      from: "follows",
      localField: "authorId",
      foreignField: "followedUserId",
      as: "followers"
    }
  },
  {
    $match: {
      "followers.followerId": currentUserId,
      "createdAt": { $gte: new Date(Date.now() - 7*24*60*60*1000) } // Last 7 days
    }
  },
  // Add author information
  {
    $lookup: {
      from: "users",
      localField: "authorId",
      foreignField: "_id",
      as: "author"
    }
  },
  {
    $sort: { "createdAt": -1 }
  },
  {
    $limit: 50
  }
])`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Hybrid Approaches</h2>
          <p style={styles.paragraph}>
            Many modern applications use both SQL and NoSQL databases, leveraging the strengths 
            of each for different use cases within the same system.
          </p>

          <div style={styles.codeBlock}>
{`// Example: E-commerce Platform with Hybrid Architecture

// SQL Database (PostgreSQL) - Core business data
-- User accounts and authentication
CREATE TABLE users (
    user_id SERIAL PRIMARY KEY,
    email VARCHAR(255) UNIQUE NOT NULL,
    password_hash VARCHAR(255) NOT NULL,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Orders and transactions (ACID required)
CREATE TABLE orders (
    order_id SERIAL PRIMARY KEY,
    user_id INTEGER REFERENCES users(user_id),
    total_amount DECIMAL(10,2) NOT NULL,
    status VARCHAR(20) NOT NULL,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

// NoSQL Database (MongoDB) - Product catalog and content
{
  // Flexible product schema
  "_id": ObjectId("..."),
  "sku": "LAPTOP-001",
  "name": "Gaming Laptop Pro",
  "description": "High-performance gaming laptop...",
  "price": 1299.99,
  "category": "Electronics",
  "subcategories": ["Computers", "Gaming"],
  "specifications": {
    "processor": "Intel i7-12700H",
    "memory": "16GB DDR4",
    "storage": "1TB NVMe SSD",
    "graphics": "NVIDIA RTX 3070",
    "display": "15.6\" 144Hz"
  },
  "images": [
    "https://cdn.example.com/products/laptop-001-1.jpg",
    "https://cdn.example.com/products/laptop-001-2.jpg"
  ],
  "reviews": [
    {
      "userId": ObjectId("..."),
      "rating": 5,
      "title": "Excellent performance",
      "comment": "Perfect for gaming and development work",
      "date": ISODate("2024-01-10"),
      "helpful": 23
    }
  ],
  "inventory": {
    "inStock": 45,
    "reserved": 3,
    "warehouses": ["US-WEST", "US-EAST"]
  },
  "seo": {
    "metaTitle": "Gaming Laptop Pro - High Performance",
    "metaDescription": "Discover the ultimate gaming experience...",
    "keywords": ["gaming laptop", "high performance", "RTX 3070"]
  }
}

// Redis Cache - Session management and caching
// User sessions
SET session:abc123 '{"userId":1001,"role":"customer","expires":1642176000}' EX 3600

// Product cache
SET product:LAPTOP-001 '{"name":"Gaming Laptop Pro","price":1299.99,"inStock":45}' EX 300

// Shopping cart
HSET cart:user:1001 product:LAPTOP-001 1
HSET cart:user:1001 product:MOUSE-001 2

// Elasticsearch - Search and analytics
{
  // Product search index
  "mappings": {
    "properties": {
      "name": {"type": "text", "analyzer": "standard"},
      "description": {"type": "text"},
      "category": {"type": "keyword"},
      "price": {"type": "float"},
      "brand": {"type": "keyword"},
      "specifications": {"type": "object"},
      "tags": {"type": "keyword"},
      "created_at": {"type": "date"}
    }
  }
}

// Search query
GET /products/_search
{
  "query": {
    "bool": {
      "must": [
        {"match": {"name": "gaming laptop"}},
        {"range": {"price": {"gte": 1000, "lte": 2000}}}
      ],
      "filter": [
        {"term": {"category": "Electronics"}},
        {"term": {"brand": "TechBrand"}}
      ]
    }
  },
  "aggs": {
    "price_ranges": {
      "range": {
        "field": "price",
        "ranges": [
          {"to": 500},
          {"from": 500, "to": 1000},
          {"from": 1000, "to": 2000},
          {"from": 2000}
        ]
      }
    }
  }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Migration Strategies</h2>
          
          <h3 style={styles.heading}>SQL to NoSQL Migration</h3>
          <div style={styles.codeBlock}>
{`// Example: Migrating user profiles from SQL to MongoDB

// Original SQL schema
CREATE TABLE users (
    user_id INT PRIMARY KEY,
    username VARCHAR(50),
    email VARCHAR(100),
    first_name VARCHAR(50),
    last_name VARCHAR(50)
);

CREATE TABLE user_preferences (
    user_id INT,
    preference_key VARCHAR(50),
    preference_value TEXT,
    FOREIGN KEY (user_id) REFERENCES users(user_id)
);

// Target MongoDB document
{
  "_id": ObjectId("..."),
  "userId": 1001,  // Keep original ID for reference
  "username": "john_doe",
  "email": "john@example.com",
  "profile": {
    "firstName": "John",
    "lastName": "Doe",
    "fullName": "John Doe"  // Computed field
  },
  "preferences": {
    "theme": "dark",
    "notifications": true,
    "language": "en"
  },
  "metadata": {
    "migratedAt": ISODate("2024-01-15"),
    "version": "2.0"
  }
}

// Migration script (Node.js example)
const migrateUsers = async () => {
  const sqlUsers = await sqlDb.query(
    "SELECT u.*, " +
    "       GROUP_CONCAT(CONCAT(up.preference_key, ':', up.preference_value)) as prefs " +
    "FROM users u " +
    "LEFT JOIN user_preferences up ON u.user_id = up.user_id " +
    "GROUP BY u.user_id"
  );
  
  for (const user of sqlUsers) {
    const preferences = {};
    if (user.prefs) {
      user.prefs.split(',').forEach(pref => {
        const [key, value] = pref.split(':');
        preferences[key] = value;
      });
    }
    
    const mongoDoc = {
      userId: user.user_id,
      username: user.username,
      email: user.email,
      profile: {
        firstName: user.first_name,
        lastName: user.last_name,
        fullName: user.first_name + " " + user.last_name
      },
      preferences,
      metadata: {
        migratedAt: new Date(),
        version: "2.0"
      }
    };
    
    await mongoDb.collection('users').insertOne(mongoDoc);
  }
};`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Performance Considerations</h2>
          
          <h3 style={styles.heading}>SQL Database Optimization</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Indexing:</strong> Create strategic indexes on frequently queried columns</li>
            <li style={styles.listItem}><strong>Query Optimization:</strong> Use EXPLAIN to analyze query performance</li>
            <li style={styles.listItem}><strong>Partitioning:</strong> Split large tables across multiple physical storage units</li>
            <li style={styles.listItem}><strong>Connection Pooling:</strong> Reuse database connections to reduce overhead</li>
          </ul>

          <h3 style={styles.heading}>NoSQL Database Optimization</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>MongoDB:</strong> Create compound indexes, use aggregation pipelines efficiently</li>
            <li style={styles.listItem}><strong>Redis:</strong> Choose appropriate data structures, use pipelining for bulk operations</li>
            <li style={styles.listItem}><strong>Cassandra:</strong> Design partition keys carefully, avoid hot partitions</li>
            <li style={styles.listItem}><strong>DynamoDB:</strong> Distribute read/write capacity, use GSI strategically</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Decision Framework</h2>
          
          <h3 style={styles.heading}>Choose SQL When:</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>ACID transactions are critical</li>
            <li style={styles.listItem}>Complex relationships between entities</li>
            <li style={styles.listItem}>Well-defined, stable schema</li>
            <li style={styles.listItem}>Complex analytical queries</li>
            <li style={styles.listItem}>Strong consistency requirements</li>
            <li style={styles.listItem}>Existing SQL expertise in team</li>
          </ul>

          <h3 style={styles.heading}>Choose NoSQL When:</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Rapid development and iteration</li>
            <li style={styles.listItem}>Horizontal scaling requirements</li>
            <li style={styles.listItem}>Flexible or evolving schema</li>
            <li style={styles.listItem}>Unstructured or semi-structured data</li>
            <li style={styles.listItem}>High-volume, high-velocity data</li>
            <li style={styles.listItem}>Eventual consistency is acceptable</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The choice between SQL and NoSQL databases isn't binary. Understanding the strengths 
            and trade-offs of each approach allows you to make informed decisions based on your 
            specific requirements. Many successful systems use both types of databases, leveraging 
            the best of both worlds for different aspects of their application.
          </p>
          
          <p style={styles.paragraph}>
            Consider factors like data structure, scalability requirements, consistency needs, 
            team expertise, and long-term maintenance when making your decision. Remember that 
            you can always evolve your architecture as your needs change and grow.
          </p>
        </section>
      </main>
    </article>
  );
};

export default SQLvsNoSQL;
